import { type CellAddr, type RangeAddr, cellKey, type Token, tokenize } from "@package/scratchpad-utils";
import { enclosingCall } from "./complete";

export type PickContext =
  | { kind: "insert"; start: number; end: number }
  | { kind: "replace"; start: number; end: number; addr: RangeAddr };

export function pickContext(text: string, caret: number): PickContext | null {
  if (!text.startsWith("=") || caret < 1) return null;
  let tokens: Token[];
  try {
    tokens = tokenize(text.slice(1));
  } catch {
    return null;
  }
  let prev: Token | undefined;
  for (const t of tokens) {
    const start = t.span.start + 1;
    const end = t.span.end + 1;
    if (t.kind === "ref" && caret >= start && caret <= end) {
      return { kind: "replace", start, end, addr: t.addr };
    }
    if (end <= caret) prev = t;
  }
  if (!prev || prev.kind === "op" || prev.kind === "lparen" || prev.kind === "comma") {
    return { kind: "insert", start: caret, end: caret };
  }
  return null;
}

export function formatRef(a: Pick<CellAddr, "col" | "row">, b: Pick<CellAddr, "col" | "row">): string {
  const start = cellKey(Math.min(a.col, b.col), Math.min(a.row, b.row));
  const end = cellKey(Math.max(a.col, b.col), Math.max(a.row, b.row));
  return start === end ? start : `${start}:${end}`;
}

export function pickSpanAt(text: string, caret: number): { start: number; end: number } | null {
  const call = enclosingCall(text, caret, "CURVE");
  if (call) return call;
  const ctx = pickContext(text, caret);
  return ctx ? { start: ctx.start, end: ctx.end } : null;
}

export type CellRect = { x: number; y: number; width: number; height: number };

export function rangeRect(addr: RangeAddr): CellRect {
  const { start, end = start } = addr;
  const x = Math.min(start.col, end.col);
  const y = Math.min(start.row, end.row);
  return { x, y, width: Math.max(start.col, end.col) - x + 1, height: Math.max(start.row, end.row) - y + 1 };
}

export type Corner = "nw" | "ne" | "sw" | "se";

export function hitCorner(rect: CellRect, point: { x: number; y: number }, tolerance: number): Corner | null {
  const corners: Array<[Corner, number, number]> = [
    ["nw", rect.x, rect.y],
    ["ne", rect.x + rect.width, rect.y],
    ["sw", rect.x, rect.y + rect.height],
    ["se", rect.x + rect.width, rect.y + rect.height],
  ];
  for (const [corner, x, y] of corners) {
    if (Math.abs(point.x - x) <= tolerance && Math.abs(point.y - y) <= tolerance) return corner;
  }
  return null;
}

export function anchorFor(range: CellRect, corner: Corner): { col: number; row: number } {
  return {
    col: corner === "nw" || corner === "sw" ? range.x + range.width - 1 : range.x,
    row: corner === "nw" || corner === "ne" ? range.y + range.height - 1 : range.y,
  };
}

export function applyPick(
  text: string,
  target: { start: number; end: number },
  ref: string,
): { text: string; caret: number } {
  return {
    text: text.slice(0, target.start) + ref + text.slice(target.end),
    caret: target.start + ref.length,
  };
}
